import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../../../utills/api";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import { toast } from "react-toastify";
import moment from "moment";
import { useNotifications } from "../../../Components/Context/NotificationContext";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const AdminComplaints = () => {
  document.title = "HMS | Complaints";

  const [complaints, setComplaints] = useState([]);
  const [bookings, setBookings] = useState([]);

  const API_BASE = "complaints";
  const BOOKINGS_API = "bookings";
  const { addNotification } = useNotifications();
  const lastComplaintCount = useRef(0);

  // ✅ Fetch complaints periodically
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [complaintRes, bookingRes] = await Promise.all([
          axios.get(API_BASE),
          axios.get(BOOKINGS_API),
        ]);

        const complaintData = Array.isArray(complaintRes.data) ? complaintRes.data : [];
        const bookingData = Array.isArray(bookingRes.data) ? bookingRes.data : [];

        // 🔹 Merge guest info (same style as Emergency)
        const mergedData = complaintData.map((c) => {
          const booking = bookingData.find((b) => b.assignRoom === c.roomNumber);
          return {
            ...c,
            guestName: booking ? booking.name : c.name || "Unknown",
            guestRoom: booking ? booking.assignRoom : c.roomNumber || "N/A",
          };
        });

        // ✅ Logged user
const user = JSON.parse(localStorage.getItem("user"));
const role = user?.role?.toLowerCase();
const dept = user?.department;

// 🔥 APPLY FILTER
let filteredData = mergedData;

// ✅ ADMIN → no filter
if (role === "client") {
  filteredData = mergedData;
}

// ✅ MANAGER → department filter
else if (role === "manager") {
  filteredData = mergedData.filter(
    (c) =>
      c.complaintType?.toLowerCase() === dept?.toLowerCase()
  );
}

// ❌ Others (optional restriction)
else {
  filteredData = [];
}

setComplaints(filteredData);
        setBookings(bookingData);

        // 🔔 Notification logic
        const prevCount = lastComplaintCount.current || 0;
        if (mergedData.length > prevCount) {
          const newCount = mergedData.length - prevCount;
          addNotification({
            title: "New Complaint",
            message: `${newCount} new complaint${newCount > 1 ? "s" : ""} received.`,
            type: "info",
          });
          lastComplaintCount.current = mergedData.length;
        }
      } catch (err) {
        console.error("❌ Error fetching complaints:", err);
        toast.error("Failed to fetch complaints");
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 7000); // poll every 7s
    return () => clearInterval(interval);
  }, [addNotification]);

  // ✅ Delete complaint
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`);
      setComplaints((prev) => prev.filter((c) => c._id !== id));
      toast.success("Complaint deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete complaint");
    }
  };

  // ✅ Columns
  const columns = useMemo(
    () => [
      {
        id: "_id",
        header: "ID",
        accessorFn: (row) => (row._id ? row._id.slice(0, 6) + "..." : ""),
      },
      { id: "guestName", header: "Guest Name", accessorKey: "guestName" },
      { id: "guestRoom", header: "Room Number", accessorKey: "guestRoom" },
      { id: "complaintType", header: "Complaint Type", accessorKey: "complaintType" },
      { id: "description", header: "Description", accessorKey: "description" },
      {
        id: "createdAt",
        header: "Created At",
        cell: ({ row }) => (
          <div>
            <div>{moment(row.original.createdAt).format("DD-MM-YYYY")}</div>
            <div>{moment(row.original.createdAt).format("hh:mm A")}</div>
          </div>
        ),
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <Link
            className="btn btn-sm btn-soft-danger"
            onClick={() => handleDelete(row.original._id)}
          >
            <i className="las la-trash-alt"></i>
          </Link>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Manage Complaints" pageTitle="Complaints" />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <div style={{ padding: "1rem" }}>
                    <TableContainer
                      columns={columns}
                      data={complaints}
                      customPageSize={5}
                      isGlobalFilter
                      isPagination
                      SearchPlaceholder="Search complaints..."
                      className="text-nowrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminComplaints;
